/**
 * Fetchers for the staff notes + outreach log API (B3, issue #424).
 *
 * No trailing slash (matches the `apps.outreach.urls` convention). Only the
 * routes this drawer's Notes tab needs are wrapped here: list + create for
 * both notes and outreach entries. Edit/soft-delete exist on the backend but
 * are out of scope for the person drawer (issue #427) — no helper for them.
 *
 * Every write derives the actor from the session server-side (`request.user`)
 * — there is no `author`/`logged_by` field to pass here even if a caller
 * wanted to; the backend ignores it.
 */

import { apiRequest } from "./client";
import type {
  CaseAcknowledgementCreated,
  CreateCaseAcknowledgementInput,
  CreateOutreachEntryInput,
  OutreachEntry,
  StaffNote,
} from "./outreach.types";

export function getPersonNotes(personId: number): Promise<StaffNote[]> {
  return apiRequest<StaffNote[]>(`/outreach/people/${personId}/notes`);
}

export function createPersonNote(
  personId: number,
  body: string,
): Promise<StaffNote> {
  return apiRequest<StaffNote>(`/outreach/people/${personId}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
}

/**
 * `GET /api/outreach/people/<id>/entries` — the person's touchpoint log.
 * `queueKey` (issue #599) narrows it to one desk CASE's attempt sequence;
 * omitted returns every touchpoint, cased or not (the person drawer's
 * behaviour, unchanged).
 */
export function getPersonOutreachEntries(
  personId: number,
  queueKey?: string,
): Promise<OutreachEntry[]> {
  const qs = queueKey ? `?queue_key=${encodeURIComponent(queueKey)}` : "";
  return apiRequest<OutreachEntry[]>(`/outreach/people/${personId}/entries${qs}`);
}

export function createPersonOutreachEntry(
  personId: number,
  input: CreateOutreachEntryInput,
): Promise<OutreachEntry> {
  return apiRequest<OutreachEntry>(`/outreach/people/${personId}/entries`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * `POST /api/outreach/people/<id>/acknowledgements` — mark a review-queue
 * case "expected / leave as-is" (issue #503 Part 2 item 3). 409s if the
 * person already has an active acknowledgement on this `queue_key`.
 *
 * Pass `contact_channel` (issue #599 follow-up) to log the contact attempt
 * against the same case in the SAME request. Two people share these queues,
 * so a 409 here is routine, not exceptional — writing the attempt separately
 * left it behind whenever the review lost the race, and the retry counted
 * the same phone call twice.
 */
export function createCaseAcknowledgement(
  personId: number,
  input: CreateCaseAcknowledgementInput,
): Promise<CaseAcknowledgementCreated> {
  return apiRequest<CaseAcknowledgementCreated>(
    `/outreach/people/${personId}/acknowledgements`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

/**
 * `DELETE /api/outreach/acknowledgements/<id>` — un-acknowledge (soft
 * delete only; the row stays on record for audit).
 */
export function deleteCaseAcknowledgement(ackId: number): Promise<void> {
  return apiRequest<void>(`/outreach/acknowledgements/${ackId}`, {
    method: "DELETE",
  });
}
